import React from "react";
import { Button, Spinner } from "react-bootstrap";
import Icon from "@mdi/react";
import {
  mdiCloseCircleOutline,
  mdiImagePlusOutline,
  mdiPencilOutline,
} from "@mdi/js";
import { Images } from "react-bootstrap-icons";
import { ACCEPTED_IMAGE_TYPES } from "./editItemFormConstants";

// Shared photo grid + dropzone for S3 and the S4 observation field. Both
// upload immediately (see AS-890) and pass `uploadsInFlight` so the Browse /
// Add Photo buttons show a disabled "Adding..." spinner while uploads run.
const EditPhotoArea = ({
  existingImages,
  pendingPhotos,
  itemName,
  isDragOver,
  uploadsInFlight = 0,
  onDragOver,
  onDragLeave,
  onDrop,
  onBrowseClick,
  onFileInputChange,
  photoInputRef,
  onRemoveExisting,
  onRemovePending,
  onOpenLightbox,
  onMarkupExisting,
}) => {
  const hasAnyPhoto = existingImages.length > 0 || pendingPhotos.length > 0;
  const isUploading = uploadsInFlight > 0;

  return (
    <>
      <div
        className={`edit-form-photo-area${hasAnyPhoto ? " has-photos" : ""}${
          isDragOver ? " drag-over" : ""
        }`}
        onDragOver={onDragOver}
        onDragEnter={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        {hasAnyPhoto ? (
          <div className="edit-images-grid">
            {existingImages.map((image, idx) => (
              <div key={image.id} className="edit-image-container">
                <img
                  src={
                    image.annotated_image?.presigned_url ||
                    image.presigned_url
                  }
                  alt={itemName || "Item"}
                />
                <div className="edit-image-hover-overlay">
                  <button
                    type="button"
                    className="edit-image-close-btn"
                    onClick={() => onRemoveExisting(image.id)}
                    aria-label="Delete photo"
                  >
                    <Icon path={mdiCloseCircleOutline} size={1} />
                  </button>
                  <div className="edit-image-hover-actions">
                    {onMarkupExisting && (
                      <Button
                        size="sm"
                        className="edit-image-markup-btn"
                        onClick={() => onMarkupExisting(image)}
                      >
                        <Icon path={mdiPencilOutline} size={0.667} />
                        Markup
                      </Button>
                    )}
                    <Button
                      size="sm"
                      className="edit-image-view-btn"
                      onClick={() => onOpenLightbox(idx)}
                    >
                      View
                    </Button>
                  </div>
                </div>
              </div>
            ))}
            {pendingPhotos.map(({ id, previewUrl }, idx) => (
              <div key={id} className="edit-image-container">
                <img src={previewUrl} alt="pending upload" />
                <div className="edit-image-hover-overlay">
                  <button
                    type="button"
                    className="edit-image-close-btn"
                    onClick={() => onRemovePending(id)}
                    aria-label="Remove photo"
                  >
                    <Icon path={mdiCloseCircleOutline} size={1} />
                  </button>
                  <div className="edit-image-hover-actions">
                    <Button
                      size="sm"
                      className="edit-image-view-btn"
                      onClick={() =>
                        onOpenLightbox(existingImages.length + idx)
                      }
                    >
                      View
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="edit-form-photo-empty">
            <Images size={48} className="edit-form-photo-empty-icon" />
            <div className="edit-form-photo-empty-text">
              <span>Drop Photos or</span>
              <button
                type="button"
                className="edit-form-photo-browse-btn"
                onClick={onBrowseClick}
                disabled={isUploading}
              >
                {isUploading ? "Adding..." : "Browse"}
              </button>
            </div>
          </div>
        )}

        {isDragOver && (
          <div className="edit-form-photo-drop-overlay">
            <span>Drop Photos to Upload</span>
          </div>
        )}

        <input
          ref={photoInputRef}
          type="file"
          accept={ACCEPTED_IMAGE_TYPES}
          multiple
          onChange={onFileInputChange}
          style={{ display: "none" }}
        />
      </div>

      {hasAnyPhoto && (
        <div className="edit-form-photo-add-more">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={onBrowseClick}
            className="edit-form-photo-add-more-btn"
            disabled={isUploading}
          >
            {isUploading ? (
              <>
                <Spinner animation="border" size="sm" /> Adding...
              </>
            ) : (
              <>
                <Icon path={mdiImagePlusOutline} size={0.833} /> Add Photo
              </>
            )}
          </Button>
        </div>
      )}
    </>
  );
};

export default EditPhotoArea;
